import React, { useState, useMemo } from "react";
import {
  Form,
  Button,
  Badge,
  Spinner,
  Table,
  Alert,
  Modal,
} from "react-bootstrap";
import { FaPlus, FaShieldAlt, FaTrash, FaKey, FaCopy, FaCheck } from "react-icons/fa";
import { useGetRolesWithPermissions } from "../../hooks/useGetRolesWithPermissions";
import { useCreateApiKey } from "../../hooks/useCreateApiKey";
import { useGetApiKeys } from "../../hooks/useGetApiKeys";

// ─── Permissions matrix ──────────────────────────────────────────────────────

const PermissionsMatrix = ({ permissionsByCategory, selectedIds, onToggle, onToggleCategory }) => (
  <div className="border rounded p-0">
    <div className="table-responsive" style={{ maxHeight: "340px", overflowY: "auto" }}>
      <Table size="sm" className="mb-0">
        <thead className="bg-body-secondary sticky-top">
          <tr>
            <th className="fw-bold py-2 px-3">Permission</th>
            <th className="fw-bold py-2 px-3 text-center" style={{ width: "80px" }}>
              Activée
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(permissionsByCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, perms]) => {
              const catIds = perms.map((p) => p.id);
              const allChecked = catIds.every((id) => selectedIds.includes(id));
              const someChecked = catIds.some((id) => selectedIds.includes(id));
              return (
                <React.Fragment key={category}>
                  <tr className="table-active">
                    <td className="fw-bold py-2 px-3 small">📋 {category}</td>
                    <td className="py-2 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked && !allChecked;
                        }}
                        onChange={() => onToggleCategory(perms)}
                        title="Tout sélectionner / désélectionner"
                        style={{ cursor: "pointer", transform: "scale(1.2)" }}
                      />
                    </td>
                  </tr>
                  {perms.map((perm) => {
                    const isChecked = selectedIds.includes(perm.id);
                    return (
                      <tr key={perm.id} className={isChecked ? "bg-primary bg-opacity-10" : ""}>
                        <td className="py-2 px-3 ps-4">
                          <code className="text-body">{perm.nom}</code>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <Form.Check
                            type="checkbox"
                            id={`perm-${perm.id}`}
                            checked={isChecked}
                            onChange={() => onToggle(perm.id)}
                            className="d-inline-block"
                            style={{ cursor: "pointer", transform: "scale(1.2)" }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
        </tbody>
      </Table>
    </div>
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

const normalizeApiKeys = (payload) => {
  const list =
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.keys) && payload.keys) ||
    (Array.isArray(payload) && payload) ||
    [];

  return list.map((k, index) => {
    const rawPermissions = k?.permissions;
    const permissions = Array.isArray(rawPermissions)
      ? rawPermissions
      : typeof rawPermissions === "string" && rawPermissions.trim()
      ? rawPermissions.split(",").map((p) => p.trim()).filter(Boolean)
      : [];

    return {
      id: k?.id ?? k?.clef_id ?? k?.key_id ?? k?.uuid ?? `server-${index}`,
      name: k?.clef ?? k?.name ?? k?.nom ?? k?.module ?? "Clé API",
      module: k?.module ?? "",
      expiryDate: k?.expiration ?? k?.expiryDate ?? k?.expires_at ?? "",
      permissions,
      createdAt: k?.created_at
        ? new Date(k.created_at).toLocaleDateString("fr-FR")
        : k?.createdAt ?? "—",
    };
  });
};

const API = ({ titre }) => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState("form"); // 'form' | 'success'
  const [formData, setFormData] = useState({ name: "", module: "", expiryDate: "", permissions: [] });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [newlyCreatedKeys, setNewlyCreatedKeys] = useState([]);
  const [hiddenKeyIds, setHiddenKeyIds] = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const createApiKey = useCreateApiKey();
  const { data: apiKeysResponse, isLoading: apiKeysLoading } = useGetApiKeys();
  const persistedKeys = useMemo(() => normalizeApiKeys(apiKeysResponse), [apiKeysResponse]);
  const apiKeys = useMemo(() => {
    const merged = [...newlyCreatedKeys, ...persistedKeys];
    const seen = new Set();

    return merged.filter((keyItem) => {
      if (hiddenKeyIds.includes(keyItem.id)) return false;
      if (seen.has(keyItem.id)) return false;
      seen.add(keyItem.id);
      return true;
    });
  }, [newlyCreatedKeys, persistedKeys, hiddenKeyIds]);

  const { data: rolesData, isLoading: permissionsLoading, isError: permissionsError } =
    useGetRolesWithPermissions();

  const allPermissions = useMemo(() => {
    const roles = rolesData?.data || [];
    const map = new Map();
    roles.forEach((role) => {
      role.permissions?.forEach((perm) => {
        if (!map.has(perm.id)) {
          map.set(perm.id, {
            id: perm.id,
            nom: perm.name,
            categorie: perm.name.split("_")[1] || "Autres",
          });
        }
      });
    });
    return Array.from(map.values());
  }, [rolesData]);

  const permissionsByCategory = useMemo(
    () =>
      allPermissions.reduce((acc, perm) => {
        const cat = perm.categorie || "Autres";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(perm);
        return acc;
      }, {}),
    [allPermissions]
  );

  const openModal = () => {
    setFormData({ name: "", module: "", expiryDate: "", permissions: [] });
    setGeneratedKey(null);
    setCopied(false);
    setStep("form");
    setShowModal(true);
  };

  const togglePermission = (id) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(id)
        ? prev.permissions.filter((p) => p !== id)
        : [...prev.permissions, id],
    }));
  };

  const toggleCategory = (perms) => {
    const catIds = perms.map((p) => p.id);
    const allChecked = catIds.every((id) => formData.permissions.includes(id));
    setFormData((prev) => ({
      ...prev,
      permissions: allChecked
        ? prev.permissions.filter((id) => !catIds.includes(id))
        : [...new Set([...prev.permissions, ...catIds])],
    }));
  };

  const formatExpiration = (datetimeLocal) => {
    // datetime-local gives "YYYY-MM-DDTHH:MM", API expects "YYYY-MM-DD HH:MM:SS"
    if (!datetimeLocal) return "";
    return datetimeLocal.replace("T", " ") + ":00";
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    createApiKey.mutate(
      {
        clef: formData.name,
        module: formData.module,
        permissions: formData.permissions,
        expiration: formatExpiration(formData.expiryDate),
      },
      {
        onSuccess: (response) => {
          const key =
            typeof response === "string"
              ? response
              : response?.data?.key ?? response?.key ?? response?.clef ?? "";
          const newEntry = {
            id: Date.now(),
            name: formData.name,
            module: formData.module,
            expiryDate: formData.expiryDate,
            permissions: formData.permissions,
            createdAt: new Date().toLocaleDateString("fr-FR"),
          };
          setGeneratedKey(key);
          setNewlyCreatedKeys((prev) => [newEntry, ...prev]);
          setStep("success");
        },
      }
    );
  };

  const handleCopy = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevoke = (id) => {
    setNewlyCreatedKeys((prev) => prev.filter((k) => k.id !== id));
    setHiddenKeyIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setRevokeTarget(null);
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <>
      {titre && (
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800 font-weight-bold">{titre}</h1>
        </div>
      )}

      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0 d-flex align-items-center gap-2">
          <FaKey /> Clés API
        </h5>
        <Button variant="primary" size="sm" onClick={openModal}>
          <FaPlus className="me-2" />
          Nouvelle clé
        </Button>
      </div>

      {/* ── Keys list ── */}
      <div className="card shadow mb-4">
        <div className="card-body p-0">
          {apiKeysLoading ? (
            <div className="text-center text-muted py-5">
              <Spinner animation="border" size="sm" />
              <div className="mt-2">Chargement des clés API…</div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaKey size={32} className="mb-3 opacity-25" />
              <p className="mb-1">Aucune clé API générée</p>
              <small>Cliquez sur "Nouvelle clé" pour en créer une.</small>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead className="bg-body-secondary">
                  <tr>
                    <th className="py-3 px-3">Nom</th>
                    <th className="py-3 px-3">Créée le</th>
                    <th className="py-3 px-3">Expiration</th>
                    <th className="py-3 px-3">Permissions</th>
                    <th className="py-3 px-3">Statut</th>
                    <th className="py-3 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => {
                    const expired = isExpired(k.expiryDate);
                    return (
                      <tr key={k.id}>
                        <td className="py-3 px-3 fw-semibold">{k.name}</td>
                        <td className="py-3 px-3 text-muted small">{k.createdAt}</td>
                        <td className="py-3 px-3 small">
                          {k.expiryDate
                            ? new Date(k.expiryDate).toLocaleDateString("fr-FR")
                            : "—"}
                        </td>
                        <td className="py-3 px-3">
                          <Badge bg="secondary">
                            {k.permissions.length} permission{k.permissions.length !== 1 ? "s" : ""}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <Badge bg={expired ? "danger" : "success"}>
                            {expired ? "Expirée" : "Active"}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-end">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setRevokeTarget(k.id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            {step === "form" ? "Nouvelle clé API" : "Clé générée avec succès"}
          </Modal.Title>
        </Modal.Header>

        {step === "form" ? (
          <Form onSubmit={handleGenerate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Nom de la clé</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: sk-webhook-prod"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Module</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Application mobile, Webhook prod…"
                  value={formData.module}
                  onChange={(e) => setFormData((prev) => ({ ...prev, module: e.target.value }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Date d'expiration</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-1">
                <Form.Label className="fw-semibold d-flex align-items-center gap-2 mb-2">
                  <FaShieldAlt size={13} /> Permissions
                </Form.Label>

                {permissionsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2 text-muted">Chargement…</span>
                  </div>
                ) : permissionsError ? (
                  <Alert variant="danger">Erreur lors du chargement des permissions</Alert>
                ) : (
                  <PermissionsMatrix
                    permissionsByCategory={permissionsByCategory}
                    selectedIds={formData.permissions}
                    onToggle={togglePermission}
                    onToggleCategory={toggleCategory}
                  />
                )}
                <small className="text-muted mt-1 d-block">
                  Cochez une catégorie pour tout sélectionner / désélectionner.
                </small>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  createApiKey.isPending ||
                  !formData.name ||
                  !formData.module ||
                  !formData.expiryDate ||
                  formData.permissions.length === 0
                }
              >
                {createApiKey.isPending ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Génération…
                  </>
                ) : (
                  <>
                    <FaPlus className="me-2" />
                    Générer
                  </>
                )}
              </Button>
              {createApiKey.isError && (
                <Alert variant="danger" className="mt-2 mb-0 py-2">
                  <small>{createApiKey.error?.message ?? "Erreur lors de la génération"}</small>
                </Alert>
              )}
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body>
              <div className="text-center mb-4">
                <div
                  className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: 56, height: 56 }}
                >
                  <FaCheck className="text-success" size={22} />
                </div>
                <h6 className="fw-bold mb-1">Clé générée</h6>
                <p className="text-muted small mb-0">
                  Copiez cette clé maintenant — vous ne pourrez plus la voir.
                </p>
              </div>

              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  value={generatedKey}
                  readOnly
                  className="font-monospace"
                  style={{
                    backgroundColor: "var(--bs-body-bg)",
                    color: "var(--bs-body-color)",
                    borderColor: "var(--bs-border-color)",
                  }}
                />
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  className="text-body"
                  onClick={handleCopy}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </Button>
              </div>

              <Alert variant="danger" className="py-2 mb-0">
                <small>
                  ⚠️ <strong>Important :</strong> Cette clé ne sera plus visible après fermeture.
                  Conservez-la en lieu sûr.
                </small>
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => setShowModal(false)}>
                Fermer
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* ── Revoke confirm ── */}
      <Modal show={!!revokeTarget} onHide={() => setRevokeTarget(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">Révoquer la clé</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted small">
          Cette action est irréversible. La clé sera immédiatement désactivée.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setRevokeTarget(null)}>
            Annuler
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleRevoke(revokeTarget)}>
            Révoquer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default API;